import React, { useEffect, useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemSecondaryAction from '@material-ui/core/ListItemSecondaryAction';
import ListItemText from '@material-ui/core/ListItemText';
import ListSubheader from '@material-ui/core/ListSubheader';
import Switch from '@material-ui/core/Switch';
import DesktopAccessDisabledIcon from '@material-ui/icons/DesktopAccessDisabled';
import LocalHospitalIcon from '@material-ui/icons/LocalHospital';
import HomeIcon from '@material-ui/icons/Home';
import SendIcon from '@material-ui/icons/Send';
import GetAppIcon from '@material-ui/icons/GetApp';
import TextField from '@material-ui/core/TextField';
import emailjs, { EmailJSResponseStatus } from 'emailjs-com';
import * as XLSX from 'xlsx';

import { generateClient } from 'aws-amplify/api';
import { listESP32SensorData, listBloodPressureData } from './graphql/queries';
import { ESP32SensorData, ModelSortDirection, BloodPressureData } from './API'
import { getStatus } from './HealthInfo'
import CircularProgress from '@material-ui/core/CircularProgress';


const useStyles = makeStyles((theme) => ({
    root: {
        width: '100%',
        backgroundColor: theme.palette.background.paper,
        opacity: '95%'
    },
}));

interface ApiResponse {
    oled_data: {
        oled_status: boolean;
    };
    bp_data: {
        diastolic: number;
        systolic: number;
        heart_rate: number;
    };
}

type OledStatusPayload = {
    device_id: string;
    oled_status: boolean;
};

class OperationError extends Error {
    constructor(message: string) {
        super(message);
        this.name = 'OperationError';
    }
}

// Send Email
export const sendEmail = (recipientEmail: string, message: string, client: string) => {
    const templateParams: Record<string, unknown> = {
        to_email: recipientEmail,
        client: client,
        message: message
    };

    emailjs.send('service_pzu072e', 'template_j4wzf2v', templateParams, 'aQy5pEh616gdx24Ze')
        .then((response: EmailJSResponseStatus) => {
            console.log('Email sent successfully:', response);
            alert('Health report sent!');
        }, (error: EmailJSResponseStatus) => {
            console.error('Error sending email:', error);
            alert('Failed to send email. Please try again.');
        });
};

const ControlPanel = () => {
    const classes = useStyles();
    const client = generateClient();

    // Initialize states for switches
    const [checked, setChecked] = useState<string[]>(() => {
        const savedChecked = localStorage.getItem('checked');
        return savedChecked ? JSON.parse(savedChecked) : ['oled'];
    });

    // State to manage loading status
    const [oledLoading, setOledLoading] = useState(false);

    // Initialize states for emails with localStorage
    const [doctorEmail, setDoctorEmail] = useState(() => localStorage.getItem('doctorEmail') || '');
    const [familyEmail, setFamilyEmail] = useState(() => localStorage.getItem('familyEmail') || '');

    // Validation error state
    const [doctorEmailError, setDoctorEmailError] = useState(false);
    const [familyEmailError, setFamilyEmailError] = useState(false);

    // Handle toggle for switches
    const handleToggle = (value: string) => async () => {
        if (oledLoading) return;  // Prevent further clicks if the button is in a loading state

        const currentIndex = checked.indexOf(value);
        const newChecked = [...checked];

        // Set loading to true when the action starts
        setOledLoading(true);

        // Optimistically update UI
        if (currentIndex === -1) {
            newChecked.push(value);  // Add value to the checked list
        } else {
            newChecked.splice(currentIndex, 1);  // Remove value from the checked list
        }

        // Update checked state and save to localStorage
        setChecked(newChecked);
        localStorage.setItem('checked', JSON.stringify(newChecked));

        try {
            // Attempt to update OLED status
            if (value === 'oled') {
                if (currentIndex === -1) {
                    await updateOledStatus(true);  // Turn on OLED if not already checked
                } else {
                    await updateOledStatus(false);  // Turn off OLED if currently checked
                }
            }
        } catch (error) {
            console.error('Error updating OLED status:', error);
            alert('Failed to change OLED Screen status. Please try again.');

            // Rollback UI changes if API call fails
            if (currentIndex === -1) {
                newChecked.splice(newChecked.indexOf(value), 1);  // Remove the value from checked
            } else {
                newChecked.push(value);  // Add the value back to checked
            }
            setChecked(newChecked);
            localStorage.setItem('checked', JSON.stringify(newChecked));
        } finally {
            // Set loading to false once the operation is complete (success or failure)
            setOledLoading(false);
        }
    };


    // Handle email input changes with validation
    const handleEmailChange = (setter: React.Dispatch<React.SetStateAction<string>>, validator: React.Dispatch<React.SetStateAction<boolean>>) => (event: React.ChangeEvent<HTMLInputElement>) => {
        const email = event.target.value;
        setter(email);

        // If the email is empty, reset validation state
        if (email === '') {
            validator(false); // No error when input is cleared
            localStorage.removeItem(event.target.id === 'doctor-email-input' ? 'doctorEmail' : 'familyEmail');
            return;
        }

        // Simple email validation regex
        const isValidEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
        validator(!isValidEmail); // set error state

        // Save to localStorage only if valid
        if (isValidEmail) {
            localStorage.setItem(event.target.id === 'doctor-email-input' ? 'doctorEmail' : 'familyEmail', email);
        }
    };

    // Fetch the OLED status from the API on component mount
    const getStatusFromApi = async () => {
        try {
            const response = await fetch('https://41ukbqzob0.execute-api.ap-southeast-2.amazonaws.com/status');
            if (response.ok) {
                const jsonResponse: ApiResponse = await response.json();
                const oledStatus = jsonResponse.oled_data?.oled_status ?? false;

                // Check and update 'oled' status in checked state
                setChecked((prevChecked) => {
                    const isOledChecked = prevChecked.includes('oled');

                    // If OLED is enabled and not already checked, add it
                    if (oledStatus && !isOledChecked) {
                        const newChecked = [...prevChecked, 'oled'];
                        localStorage.setItem('checked', JSON.stringify(newChecked));
                        return newChecked;
                    }
                    // If OLED is disabled and is currently checked, remove it
                    if (!oledStatus && isOledChecked) {
                        const newChecked = prevChecked.filter((item) => item !== 'oled');
                        localStorage.setItem('checked', JSON.stringify(newChecked));
                        return newChecked;
                    }
                    return prevChecked;
                });
            } else {
                console.error('HTTP request failed with status:', response.status);
            }
        } catch (error) {
            console.error('Error fetching or parsing data:', error);
        }
    };

    // Control OLED
    const updateOledStatus = async (oled_status: boolean) => {
        const apiUrl = 'https://41ukbqzob0.execute-api.ap-southeast-2.amazonaws.com/oled';
        const payload: OledStatusPayload = {
            device_id: "esp32_001",
            oled_status: oled_status,
        };
        try {
            const response = await fetch(apiUrl, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(payload),
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(`Error updating OLED status: ${errorData.message}`);
            }

            const responseData = await response.json();
            console.log('OLED status updated successfully:', responseData);
        } catch (error) {
            throw new OperationError('fail');
        }
    }

    // Call the API once when the component is mounted
    useEffect(() => {
        getStatusFromApi();
    }, []);

    const handleEmailSend = async () => {
        // Get checked array to check if 'doctor' or 'family' is enabled
        const checked = JSON.parse(localStorage.getItem('checked') || '[]');
        const doctorEmail = localStorage.getItem('doctorEmail');
        const familyEmail = localStorage.getItem('familyEmail');
        if ((checked.includes('doctor') && doctorEmail) || (checked.includes('family') && familyEmail)) {
            const message = await getEmailMessage();

            // Check and send email to the doctor if enabled and email is valid
            if (checked.includes('doctor') && doctorEmail) {
                console.log('Email sending to doctor:', doctorEmail);
                sendEmail(doctorEmail, message, "Doctor");
            }

            // Check and send email to the family if enabled and email is valid
            if (checked.includes('family') && familyEmail) {
                console.log('Email sending to family:', familyEmail);
                sendEmail(familyEmail, message, "Family Members");
            }
        }
    };

    const getEmailMessage = async () => {
        let sensorData: ESP32SensorData[] = [];
        let bloodPressureData: BloodPressureData[] = [];

        // Fetch ESP32 sensor data
        try {
            const apiData = await client.graphql({
                query: listESP32SensorData,
                variables: {
                    device_id: 'esp32_001',
                    limit: 1,
                    sortDirection: ModelSortDirection.DESC
                }
            });
            const items = apiData.data.listESP32SensorData?.items ?? [];
            sensorData = items as ESP32SensorData[];
        } catch (err) {
            console.error('Error fetching sensor data:', err);
        }

        // Fetch blood pressure data
        try {
            const apiData = await client.graphql({
                query: listBloodPressureData,
                variables: {
                    device_id: 'bp_device_001',
                    limit: 1,
                    sortDirection: ModelSortDirection.DESC
                }
            });
            const items = apiData.data.listBloodPressureData?.items ?? [];
            bloodPressureData = items as BloodPressureData[];
        } catch (err) {
            console.error('Error fetching blood pressure data:', err);
        }

        // Extract the relevant data points
        const systolic = bloodPressureData[0]?.systolic;
        const diastolic = bloodPressureData[0]?.diastolic;
        const heart_rate_2 = bloodPressureData[0]?.heart_rate;
        const pressure = sensorData[0]?.pressure;
        const temperature = sensorData[0]?.temperature;
        const humidity = sensorData[0]?.humidity;
        const spo2 = sensorData[0]?.spo2;
        const heart_rate = sensorData[0]?.heart_rate;

        // Get the status and color using the getStatus function
        const systolicStatus = getStatus(systolic, 'systolic');
        const diastolicStatus = getStatus(diastolic, 'diastolic');
        const heartRateStatus2 = getStatus(heart_rate_2, 'heart_rate');
        const pressureStatus = getStatus(pressure, 'pressure');
        const temperatureStatus = getStatus(temperature, 'temperature');
        const humidityStatus = getStatus(humidity, 'humidity');
        const spo2Status = getStatus(spo2, 'spo2');
        const heartRateStatus = getStatus(heart_rate, 'heart_rate');

        // Construct the message
        const message = `
        Systolic: ${systolic ?? 'N/A'} mmHg
            - Status: ${systolicStatus.status}
        Diastolic: ${diastolic ?? 'N/A'} mmHg
            - Status: ${diastolicStatus.status}
        Heart Rate (Sphygmometer): ${heart_rate ?? 'N/A'} bpm
            - Status: ${heartRateStatus2.status}
        Pressure: ${pressure ?? 'N/A'} hPa
            - Status: ${pressureStatus.status}
        Temperature: ${temperature ?? 'N/A'} °C
            - Status: ${temperatureStatus.status}
        Humidity: ${humidity ?? 'N/A'} %
            - Status: ${humidityStatus.status}
        SpO2: ${spo2 ?? 'N/A'} %
            - Status: ${spo2Status.status}
        Heart Rate (Real Time): ${heart_rate ?? 'N/A'} bpm
            - Status: ${heartRateStatus.status}
        `;
        return message;
    };

    const exportToExcel = async () => {
        let sensorData: ESP32SensorData[] = [];
        let bloodPressureData: BloodPressureData[] = [];

        // Fetch ESP32 sensor data
        try {
            const sensorApiData = await client.graphql({
                query: listESP32SensorData,
                variables: {
                    device_id: 'esp32_001',
                    limit: 300,
                    sortDirection: ModelSortDirection.DESC
                }
            });
            const sensorItems = sensorApiData.data.listESP32SensorData?.items ?? [];
            sensorData = sensorItems as ESP32SensorData[];
        } catch (err) {
            console.error('Error fetching ESP32 sensor data:', err);
        }

        // Fetch blood pressure data
        try {
            const bloodPressureApiData = await client.graphql({
                query: listBloodPressureData,
                variables: {
                    device_id: 'bp_device_001',
                    limit: 50,
                    sortDirection: ModelSortDirection.DESC
                }
            });
            const bloodPressureItems = bloodPressureApiData.data.listBloodPressureData?.items ?? [];
            bloodPressureData = bloodPressureItems as BloodPressureData[];
        } catch (err) {
            console.error('Error fetching blood pressure data:', err);
        }

        // Convert UTC timestamp to local time
        const formatToLocalTime = (utcString: string) => {
            const date = new Date(Date.parse(utcString + 'Z'));
            return date.toLocaleString(); // Converts to local time format
        };

        // Prepare sensor data for the first sheet
        const sensorDataForExcel = sensorData.map((sensorItem) => ({
            'Device ID': sensorItem.device_id,
            'Time': formatToLocalTime(sensorItem.timestamp),  // Convert to local time
            'Temperature (C)': sensorItem.temperature ?? 'N/A',
            'Pressure (hPa)': sensorItem.pressure ?? 'N/A',
            'Humidity (%)': sensorItem.humidity ?? 'N/A',
            'SpO2 (%)': sensorItem.spo2 ?? 'N/A',
            'Heart Rate - Real Time (bpm)': sensorItem.heart_rate ?? 'N/A'
        }));

        // Prepare blood pressure data for the second sheet
        const bloodPressureDataForExcel = bloodPressureData.map((bpItem) => ({
            'Device ID': bpItem.device_id,
            'Time': formatToLocalTime(bpItem.date),  // Convert to local time
            'Systolic (mmHg)': bpItem.systolic ?? 'N/A',
            'Diastolic (mmHg)': bpItem.diastolic ?? 'N/A',
            'Heart Rate (bpm)': bpItem.heart_rate ?? 'N/A'
        }));

        // Generate Excel worksheets
        const sensorWorksheet = XLSX.utils.json_to_sheet(sensorDataForExcel);
        const bloodPressureWorksheet = XLSX.utils.json_to_sheet(bloodPressureDataForExcel);

        // Create a new workbook and append both sheets
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, sensorWorksheet, "Sensor Data");
        XLSX.utils.book_append_sheet(workbook, bloodPressureWorksheet, "Blood Pressure Data");

        // Convert workbook to a binary Blob
        const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
        const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });

        // Create a link element and trigger a download
        const link = document.createElement('a');
        const fileName = `health_data_${new Date().toISOString().slice(0, 10)}.xlsx`;
        link.href = URL.createObjectURL(blob);
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };


    return (
        <div>
            <h1>Control Panel</h1>
            <List subheader={<ListSubheader>Settings</ListSubheader>} className={classes.root}>
                <ListItem>
                    <ListItemIcon>
                        <DesktopAccessDisabledIcon />
                    </ListItemIcon>
                    <ListItemText id="switch-oled-screen" primary="OLED Screen" />
                    <ListItemSecondaryAction>
                        {
                            oledLoading ? (<CircularProgress size={24} />  // Show loading spinner when loading
                            ) : (
                                <Switch
                                    edge="end"
                                    onChange={handleToggle('oled')}
                                    checked={checked.indexOf('oled') !== -1}
                                    disabled={oledLoading}  // Disable the switch during loading to prevent further interaction
                                    inputProps={{ 'aria-labelledby': 'switch-oled-screen' }}
                                />
                            )
                        }
                    </ListItemSecondaryAction>
                </ListItem>
                <ListItem>
                    <ListItemIcon>
                        <LocalHospitalIcon />
                    </ListItemIcon>
                    <ListItemText id="switch-email-to-doctor" primary="Email To Doctor" />
                    <ListItemSecondaryAction>
                        <Switch
                            edge="end"
                            onChange={handleToggle('doctor')}
                            checked={checked.indexOf('doctor') !== -1}
                            inputProps={{ 'aria-labelledby': 'switch-email-to-doctor' }}
                        />
                    </ListItemSecondaryAction>
                </ListItem>
                <ListItem>
                    <ListItemIcon>
                        <HomeIcon />
                    </ListItemIcon>
                    <ListItemText id="switch-email-to-family" primary="Email To Family" />
                    <ListItemSecondaryAction>
                        <Switch
                            edge="end"
                            onChange={handleToggle('family')}
                            checked={checked.indexOf('family') !== -1}
                            inputProps={{ 'aria-labelledby': 'switch-email-to-family' }}
                        />
                    </ListItemSecondaryAction>
                </ListItem>
            </List>

            <List
                component="nav"
                aria-labelledby="nested-list-subheader"
                subheader={
                    <ListSubheader component="div" id="nested-list-subheader">
                        Operate
                    </ListSubheader>
                }
                className={classes.root}
            >
                <ListItem
                    button
                    onClick={handleEmailSend}
                >
                    <ListItemIcon>
                        <SendIcon />
                    </ListItemIcon>
                    <ListItemText primary="Send Email(Current Data)" />
                </ListItem>
                <ListItem
                    button
                    onClick={exportToExcel}
                >
                    <ListItemIcon>
                        <GetAppIcon />
                    </ListItemIcon>
                    <ListItemText primary="Download All Data" />
                </ListItem>
            </List>

            <List
                component="nav"
                aria-labelledby="email-input"
                subheader={
                    <ListSubheader component="div" id="email-input">
                        Email
                    </ListSubheader>
                }
                className={classes.root}
            >
                <ListItem>
                    {/* Doctor's Email Input */}
                    <TextField
                        id="doctor-email-input"
                        label="Doctor's Email"
                        style={{ margin: 8 }}
                        placeholder="Enter doctor's email"
                        fullWidth
                        margin="normal"
                        InputLabelProps={{
                            shrink: true,
                        }}
                        variant="outlined"
                        value={doctorEmail}
                        onChange={handleEmailChange(setDoctorEmail, setDoctorEmailError)}
                        error={doctorEmailError}
                        helperText={doctorEmailError ? "Invalid email address" : ""}
                    />
                </ListItem>
                <ListItem>
                    {/* Family's Email Input */}
                    <TextField
                        id="family-email-input"
                        label="Family's Email"
                        style={{ margin: 8 }}
                        placeholder="Enter family's email"
                        fullWidth
                        margin="normal"
                        InputLabelProps={{
                            shrink: true,
                        }}
                        variant="outlined"
                        value={familyEmail}
                        onChange={handleEmailChange(setFamilyEmail, setFamilyEmailError)}
                        error={familyEmailError}
                        helperText={familyEmailError ? "Invalid email address" : ""}
                    />
                </ListItem>
            </List>
        </div>
    );
};

export default ControlPanel;
